import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Collapse,
  Stack,
  IconButton,
} from '@mui/material';
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import Reveal from './Reveal';
import ContactFormDialog from './ContactFormDialog';
import { tokens } from '../theme';

const plans = [
  {
    name: 'Professional',
    price: 499,
    yearlyPrice: 4990,
    description: 'Growing teams + voice & SMS',
    popular: true,
    features: [
      { text: '50 active chats', included: true },
      { text: '5,000 messages/month', included: true },
      { text: '3 team members, 1 project', included: true },
      { text: 'Web widget + Calendar', included: true },
      { text: '30 voice minutes/mo', included: true },
      { text: '100 SMS included', included: true },
      { text: '1 phone number', included: true },
      { text: 'Priority Support', included: true },
    ],
  },
  {
    name: 'Business',
    price: 899,
    yearlyPrice: 8990,
    description: 'Scale operations',
    popular: false,
    features: [
      { text: '100 active chats', included: true },
      { text: '15,000 messages/month', included: true },
      { text: '5 team members, 3 projects', included: true },
      { text: 'AI Lead Generation + MCP', included: true },
      { text: '300 voice minutes/mo', included: true },
      { text: '1,000 SMS included', included: true },
      { text: '3 phone numbers', included: true },
      { text: 'Priority Support', included: true },
    ],
  },
  {
    name: 'Enterprise',
    price: 2499,
    yearlyPrice: 24990,
    description: 'Full-scale operations',
    popular: false,
    features: [
      { text: '500 active chats', included: true },
      { text: '50,000 messages/month', included: true },
      { text: 'Unlimited team, 10 projects', included: true },
      { text: 'Custom integrations + SLA', included: true },
      { text: '3,000 voice minutes/mo', included: true },
      { text: '10,000 SMS included', included: true },
      { text: 'Unlimited phone numbers', included: true },
      { text: 'Custom features on request', included: true },
    ],
  },
  {
    name: 'Corporate',
    price: -1, // sentinel: rendered as "Custom" — never "$ /month"
    yearlyPrice: -1,
    description: 'Custom limits & dedicated support',
    popular: false,
    contactSales: true,
    features: [
      { text: 'Custom active chats', included: true },
      { text: 'Custom messages/month', included: true },
      { text: 'Unlimited projects & team', included: true },
      { text: 'All platforms + voice, SMS & widget', included: true },
      { text: 'Custom voice minutes & numbers', included: true },
      { text: 'All AI features', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Priority support + SLA', included: true },
    ],
  },
];

type ComparisonValue = string | boolean;

interface ComparisonRow {
  label: string;
  values: ComparisonValue[];
}

interface ComparisonCategory {
  name: string;
  rows: ComparisonRow[];
}

const comparisonCategories: ComparisonCategory[] = [
  {
    name: 'Core Limits',
    rows: [
      { label: 'Active Chats', values: ['50', '100', '500', 'Custom'] },
      { label: 'Messages / Month', values: ['5,000', '15,000', '50,000', 'Custom'] },
      { label: 'Platforms', values: ['All', 'All', 'All', 'All'] },
      { label: 'Projects', values: ['1', '3', '10', 'Unlimited'] },
      { label: 'Team Members', values: ['3', '5', '10', 'Unlimited'] },
    ],
  },
  {
    name: 'Knowledge Base & Storage',
    rows: [
      { label: 'RAG Documents', values: ['100', '250', '500', 'Unlimited'] },
      { label: 'Max File Size (per doc)', values: ['100 MB', '250 MB', '500 MB', 'Custom'] },
      { label: 'Web Scraper URLs', values: ['50', '100', '200', 'Unlimited'] },
      { label: 'Data Export Formats', values: ['CSV, JSON, API', 'CSV, JSON, API, SQL', 'All + Custom', 'All + Custom'] },
    ],
  },
  {
    name: 'AI & Training',
    rows: [
      { label: 'AI Chat Features', values: [true, true, true, true] },
      { label: 'AI Test Chat (msgs/mo)', values: ['200', '500', '2,000', 'Unlimited'] },
      { label: 'AI Avatar Generations / Mo', values: ['20', '50', '200', 'Unlimited'] },
      { label: 'Custom Avatar Prompt', values: [true, true, true, true] },
      { label: 'Training Imports / Mo', values: ['200', 'Unlimited', '2,000', 'Unlimited'] },
      { label: 'Sales Strategies / Mo', values: ['50', '200', '1,000', 'Unlimited'] },
    ],
  },
  {
    name: 'Voice, SMS & Phone Numbers',
    rows: [
      { label: 'AI Voice Minutes / Mo', values: ['30', '300', '3,000', 'Custom'] },
      { label: 'SMS Included / Mo', values: ['100', '1,000', '10,000', 'Custom'] },
      { label: 'Phone Numbers', values: ['1', '3', 'Unlimited', 'Unlimited'] },
    ],
  },
  {
    name: 'Features & Integrations',
    rows: [
      { label: 'Data Export', values: [true, true, true, true] },
      { label: 'Calendar Integration', values: [true, true, true, true] },
      { label: 'MCP Tools', values: [false, true, true, true] },
      { label: 'AI Lead Generation', values: [false, true, true, true] },
      { label: 'Lead Searches / Mo', values: ['0', '500', '5,000', 'Custom'] },
      { label: 'Outreach Emails / Mo', values: ['0', '1,000', '10,000', 'Custom'] },
      { label: 'RAG Data Assistant', values: [false, true, true, true] },
      { label: 'Automation (Webhooks)', values: [false, true, true, true] },
      { label: 'E-Commerce Integration', values: [false, true, true, true] },
      { label: 'Advanced Platform Connect', values: [false, false, true, true] },
    ],
  },
  {
    name: 'Support & Services',
    rows: [
      { label: 'Email Support', values: [true, true, true, true] },
      { label: 'Priority Support', values: [true, true, true, true] },
      { label: 'Dedicated Account Manager', values: [false, false, true, true] },
      { label: 'Custom Features (1-2 wk)', values: [false, false, true, true] },
      { label: 'Custom Integrations (API/CRM/ERP)', values: [false, false, true, true] },
      { label: 'SLA Guarantee', values: [false, false, true, true] },
    ],
  },
];

function CategoryTable({ category }: { category: ComparisonCategory }) {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ '&:not(:last-child)': { borderBottom: `1px solid ${tokens.border}` } }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.8,
          cursor: 'pointer',
          transition: `background-color ${tokens.motion.micro}ms ease`,
          '&:hover': { bgcolor: tokens.bgDefault },
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', color: 'text.primary' }}>
          {category.name}
        </Typography>
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          {open ? (
            <ChevronUp size={18} strokeWidth={1.5} />
          ) : (
            <ChevronDown size={18} strokeWidth={1.5} />
          )}
        </IconButton>
      </Box>
      <Collapse in={open}>
        {/* horizontal scroll wrapper so the table never overflows on small screens */}
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              minWidth: 680,
              borderCollapse: 'collapse',
              '& th, & td': {
                px: 2,
                py: 1.2,
                fontSize: '0.78rem',
                borderBottom: `1px solid ${tokens.border}`,
                textAlign: 'center',
              },
              '& th': {
                fontWeight: 600,
                color: 'text.secondary',
                bgcolor: tokens.bgDefault,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              },
              '& td:first-of-type, & th:first-of-type': {
                textAlign: 'left',
              },
              '& td:first-of-type': {
                fontWeight: 500,
                color: 'text.secondary',
              },
              '& tbody tr:last-of-type td': {
                borderBottom: 'none',
              },
            }}
          >
            <thead>
              <tr>
                <th>Feature</th>
                {plans.map((p) => (
                  <th key={p.name}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {category.rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i}>
                      {typeof val === 'boolean' ? (
                        val ? (
                          <Check
                            size={16}
                            strokeWidth={1.5}
                            color={tokens.sage}
                            style={{ verticalAlign: 'middle' }}
                          />
                        ) : (
                          <X
                            size={14}
                            strokeWidth={1.5}
                            color="rgba(28,25,23,0.22)"
                            style={{ verticalAlign: 'middle' }}
                          />
                        )
                      ) : (
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.78rem', color: 'text.primary', fontWeight: 500 }}
                        >
                          {val}
                        </Typography>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);

  const handleBillingChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: 'monthly' | 'yearly' | null,
  ) => {
    if (newValue) setBillingCycle(newValue);
  };

  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            Pricing
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 480, mx: 'auto', mb: 4 }}
          >
            One AI doing the work of a receptionist, a support agent, and an SDR. Phone numbers, voice minutes, and SMS scale with your tier.
          </Typography>

          {/* billing cycle — cream pill, ink active state */}
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingChange}
            sx={{
              bgcolor: tokens.bgDefault,
              border: `1px solid ${tokens.border}`,
              borderRadius: 999,
              p: 0.4,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '999px !important',
                px: 3,
                py: 0.8,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: 'text.secondary',
                transition: `background-color ${tokens.motion.micro}ms ease, color ${tokens.motion.micro}ms ease`,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'text.primary',
                },
                '&.Mui-selected': {
                  bgcolor: tokens.ink,
                  color: '#FAF7F1',
                  '&:hover': {
                    bgcolor: tokens.inkHover,
                    color: '#FAF7F1',
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(250,247,241,0.18)',
                    color: '#FAF7F1',
                  },
                },
              },
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">
              Yearly
              <Chip
                label="Save 2 Months"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: tokens.sageTint,
                  color: tokens.sage,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  height: 22,
                  transition: `background-color ${tokens.motion.micro}ms ease, color ${tokens.motion.micro}ms ease`,
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Reveal>

        {/* pricing cards */}
        <Grid container spacing={2} justifyContent="center">
          {plans.map((plan, planIndex) => {
            const monthlyPrice =
              billingCycle === 'monthly'
                ? plan.price
                : Math.round(plan.yearlyPrice / 12);
            const yearlyTotal = plan.yearlyPrice;

            return (
              <Grid
                size={{ xs: 12, sm: 6, md: 6, lg: 3 }}
                key={plan.name}
              >
                <Reveal delay={planIndex * 70} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      p: 3,
                      borderRadius: `${tokens.radiusCard}px`,
                      position: 'relative',
                      bgcolor: 'background.paper',
                      border: `1px solid ${tokens.border}`,
                      borderTop: plan.popular
                        ? `3px solid ${tokens.copper}`
                        : `1px solid ${tokens.border}`,
                      boxShadow: plan.popular ? tokens.shadowLift : tokens.shadowSoft,
                      transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                      '&:hover': {
                        boxShadow: tokens.shadowLift,
                        borderColor: '#D9CDBC',
                        borderTopColor: plan.popular ? tokens.copper : '#D9CDBC',
                      },
                      overflow: 'visible',
                    }}
                  >
                    {plan.popular && (
                      <Chip
                        label="Most Popular"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -13,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: tokens.copper,
                          color: '#FFFFFF',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          borderRadius: 999,
                        }}
                      />
                    )}

                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {plan.name}
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                      {plan.contactSales ? (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: tokens.fontDisplay,
                            fontSize: '2.4rem',
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            color: 'text.primary',
                          }}
                        >
                          Custom
                        </Typography>
                      ) : (
                        <>
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: tokens.fontDisplay,
                              fontSize: '2.4rem',
                              fontWeight: 700,
                              letterSpacing: '-0.02em',
                              color: 'text.primary',
                            }}
                          >
                            ${monthlyPrice}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                              ml: 0.5,
                            }}
                          >
                            /mo
                          </Typography>
                        </>
                      )}
                    </Box>

                    {billingCycle === 'yearly' && !plan.contactSales && yearlyTotal > 0 && (
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'text.secondary',
                          mb: 1,
                        }}
                      >
                        ${yearlyTotal}/year
                      </Typography>
                    )}

                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        mb: 2,
                      }}
                    >
                      {plan.description}
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <List dense disablePadding sx={{ flex: 1 }}>
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} disableGutters sx={{ py: 0.4 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {feature.included ? (
                              <Check size={16} strokeWidth={1.5} color={tokens.sage} />
                            ) : (
                              <X size={16} strokeWidth={1.5} color="rgba(28,25,23,0.22)" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.text}
                            secondary={'subtext' in feature ? (feature as any).subtext : undefined}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.85rem',
                                color: feature.included ? 'text.primary' : 'rgba(28,25,23,0.35)',
                              },
                            }}
                            secondaryTypographyProps={{
                              sx: {
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                lineHeight: 1.4,
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {plan.contactSales ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => setContactSalesOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Contact Sales
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        href="https://app.cleolead.com/login"
                        sx={{ mt: 2 }}
                      >
                        Get Started
                      </Button>
                    )}
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>

        {/* show comparison toggle */}
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            onClick={() => setShowComparison(!showComparison)}
            endIcon={
              <ChevronDown
                size={18}
                strokeWidth={1.5}
                style={{
                  transform: showComparison ? 'rotate(180deg)' : 'none',
                  transition: `transform ${tokens.motion.transition}ms ease`,
                }}
              />
            }
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': { color: tokens.copper, bgcolor: 'transparent' },
            }}
          >
            {showComparison ? 'Hide' : 'Show'} full comparison
          </Button>
        </Box>

        <Collapse in={showComparison}>
          <Box
            sx={{
              mt: 3,
              borderRadius: `${tokens.radiusCard}px`,
              border: `1px solid ${tokens.border}`,
              bgcolor: 'background.paper',
              boxShadow: tokens.shadowSoft,
              overflow: 'hidden',
            }}
          >
            {comparisonCategories.map((category) => (
              <CategoryTable key={category.name} category={category} />
            ))}
          </Box>
        </Collapse>

        {/* included in all plans */}
        <Reveal>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 5 }}
          >
            {['SSL encryption', 'GDPR compliant', 'Email support', 'No setup fees'].map((t) => (
              <Typography
                key={t}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.82rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                }}
              >
                <Check size={15} strokeWidth={1.5} color={tokens.sage} /> {t}
              </Typography>
            ))}
          </Stack>
        </Reveal>

        <ContactFormDialog
          open={contactSalesOpen}
          onClose={() => setContactSalesOpen(false)}
          title="Contact Sales"
          subtitle="Tell us about your team and what you need. We'll put together a Corporate plan with custom limits and dedicated support."
          formType="enterprise"
          defaultSubject="Corporate plan inquiry"
          defaultMessage="Hi — I'd like to learn about the Corporate plan. My company is ..."
        />
      </Container>
    </Box>
  );
}
